import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import {
  FingerprintRounded as FingerprintIcon,
  UploadFileRounded as UploadFileIcon,
  CheckCircleRounded as CheckCircleIcon,
} from "@mui/icons-material";
import {
  captureFingerprint,
  discoverScanner,
  fingerprintFromFile,
} from "./fingerprint";
import { BLUE, BLUE_DARK, BORDER, ERROR_C, INK, INK_MUTED, SUCCESS } from "./shared";

// ─── Fingerprint capture dialog ──────────────────────────────────────────────
// Drives one finger at a time. On open it looks for a ready RD-service reader
// so the operator knows straight away whether the scanner is live; pressing
// Scan then asks the reader for one impression.
//
// Two things are deliberately non-fatal here: having no reader at all, and a
// failed scan. Fingerprints are optional — the operator can attach a template
// file the reader's own software produced, or close the dialog and leave the
// column NULL.
const FingerprintCaptureDialog = ({ open, field, label, onClose, onCaptured }) => {
  const [device, setDevice] = useState(null);
  const [probing, setProbing] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  // Look for the reader each time the dialog opens (it may have been plugged
  // in — or unplugged — since the last capture).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError("");
    setResult(null);
    setProbing(true);
    discoverScanner({ refresh: true })
      .then((d) => {
        if (!cancelled) setDevice(d);
      })
      .catch(() => {
        if (!cancelled) setDevice(null);
      })
      .finally(() => {
        if (!cancelled) setProbing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleScan = async () => {
    setError("");
    setResult(null);
    setScanning(true);
    try {
      const capture = await captureFingerprint();
      setResult(capture);
      onCaptured(field, capture);
    } catch (err) {
      setError(err?.message || "Fingerprint scan failed");
    } finally {
      setScanning(false);
    }
  };

  const handleFile = async (file) => {
    setError("");
    setResult(null);
    try {
      const capture = await fingerprintFromFile(file);
      setResult(capture);
      onCaptured(field, capture);
    } catch (err) {
      setError(err?.message || "Could not read the fingerprint file");
    }
  };

  const busy = scanning || probing;

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      PaperProps={{ sx: { borderRadius: 3, width: "100%", maxWidth: 380 } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          fontSize: "1rem",
          fontWeight: 700,
          color: INK,
          pb: 1,
        }}
      >
        <FingerprintIcon sx={{ color: BLUE }} />
        {label || "Capture Fingerprint"}
      </DialogTitle>
      <DialogContent>
        {/* Scanner status — resolved on open, refreshed after each attempt. */}
        <Typography
          sx={{ fontSize: "0.75rem", color: INK_MUTED, mb: 1.5 }}
        >
          {probing
            ? "Looking for a fingerprint scanner…"
            : device
              ? `Scanner ready — ${device.info}`
              : "No scanner detected. Plug in the reader and start its RD Service, or attach a scan file below."}
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            py: 3,
            borderRadius: 2,
            border: `2px dashed ${result ? SUCCESS : BORDER}`,
            bgcolor: result ? "#ECFDF5" : "#F8FAFC",
          }}
        >
          {scanning ? (
            <>
              <CircularProgress size={34} sx={{ color: BLUE }} />
              <Typography sx={{ fontSize: "0.8rem", color: INK_MUTED }}>
                Place the finger on the scanner…
              </Typography>
            </>
          ) : result ? (
            <>
              <CheckCircleIcon sx={{ fontSize: 38, color: SUCCESS }} />
              <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: SUCCESS }}>
                Fingerprint captured
              </Typography>
              <Typography sx={{ fontSize: "0.72rem", color: INK_MUTED }}>
                {result.source === "scanner" ? "Scanned" : "From file"}
                {result.quality != null ? ` · quality ${result.quality}` : ""}
              </Typography>
            </>
          ) : (
            <>
              <FingerprintIcon sx={{ fontSize: 44, color: BLUE, opacity: 0.55 }} />
              <Typography sx={{ fontSize: "0.8rem", color: INK_MUTED }}>
                Ready when you are
              </Typography>
            </>
          )}
        </Box>

        {error && (
          <Alert
            severity="warning"
            sx={{ mt: 1.5, fontSize: "0.78rem", py: 0.25, color: ERROR_C }}
          >
            {error}
          </Alert>
        )}

        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<FingerprintIcon />}
            disabled={busy}
            onClick={handleScan}
            sx={{
              bgcolor: BLUE,
              "&:hover": { bgcolor: BLUE_DARK },
              borderRadius: 2,
            }}
          >
            {result ? "Scan Again" : "Scan"}
          </Button>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<UploadFileIcon />}
            disabled={busy}
            onClick={() => fileInputRef.current?.click()}
            sx={{ borderRadius: 2 }}
          >
            Attach File
          </Button>
        </Stack>
        <input
          ref={fileInputRef}
          type="file"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            // Reset so re-picking the same file fires onChange again.
            e.target.value = "";
            if (file) handleFile(file);
          }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={scanning} sx={{ color: INK_MUTED }}>
          {result ? "Done" : "Skip"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FingerprintCaptureDialog;
